import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { HolidayModel } from '../../models/holiday.model';
import { environment } from '../../../../environments/environment';

// ---- Backend DTO shape -------------------------------------------------------

type BackendHolidayType = 'SPECIFIC_DATE' | 'CUSTOM_DATE';

interface HolidayDto {
  id: number;
  name: string;
  type: BackendHolidayType;
  date: string | null;       // ISO-8601 Instant string, e.g. "2024-07-04T00:00:00Z"
  dayNumber: number | null;  // Ordinal occurrence (1=First … 4=Fourth, 5=Last)
  dayOfWeek: number | null;  // 0=Sunday … 6=Saturday (matches java.time.DayOfWeek - 1)
  month: number | null;      // 1=January … 12=December
}

// ---- Mapping helpers ---------------------------------------------------------

const ORDINAL_FROM_NUMBER: Record<number, string> = {
  1: 'First',
  2: 'Second',
  3: 'Third',
  4: 'Fourth',
  5: 'Last',
};

const ORDINAL_TO_NUMBER: Record<string, number> = {
  First: 1,
  Second: 2,
  Third: 3,
  Fourth: 4,
  Last: 5,
};

// Backend dayOfWeek: 1=Monday … 7=Sunday (java.time.DayOfWeek)
const DAY_FROM_NUMBER: Record<number, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

const DAY_TO_NUMBER: Record<string, number> = {
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
  Sunday: 7,
  Day: 0,   // "Day" means nth calendar day — mapped to 0 as a sentinel
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function dtoToModel(dto: HolidayDto): HolidayModel {
  const type = dto.type === 'SPECIFIC_DATE' ? 'specific' : 'configured';
  const model: HolidayModel = {
    id: dto.id,
    name: dto.name,
    type,
  };

  if (type === 'specific' && dto.date) {
    model.date = new Date(dto.date);
    model.year = model.date.getFullYear();
  } else {
    model.ordinal = dto.dayNumber != null ? (ORDINAL_FROM_NUMBER[dto.dayNumber] ?? 'First') : 'First';
    model.dayOfWeek = dto.dayOfWeek != null ? (DAY_FROM_NUMBER[dto.dayOfWeek] ?? 'Monday') : 'Monday';
    model.month = dto.month != null ? (MONTH_NAMES[dto.month - 1] ?? 'January') : 'January';
  }

  return model;
}

function modelToDto(model: Omit<HolidayModel, 'id'> & { id?: number }): HolidayDto {
  const dto: HolidayDto = {
    id: model.id ?? 0,
    name: model.name,
    type: model.type === 'specific' ? 'SPECIFIC_DATE' : 'CUSTOM_DATE',
    date: null,
    dayNumber: null,
    dayOfWeek: null,
    month: null,
  };

  if (model.type === 'specific' && model.date) {
    // Send as ISO string (midnight UTC for the selected date)
    const d = new Date(model.date);
    dto.date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString();
  } else {
    dto.dayNumber = model.ordinal != null ? (ORDINAL_TO_NUMBER[model.ordinal] ?? 1) : 1;
    dto.dayOfWeek = model.dayOfWeek != null ? (DAY_TO_NUMBER[model.dayOfWeek] ?? 1) : 1;
    dto.month = model.month != null ? (MONTH_NAMES.indexOf(model.month) + 1) : 1;
  }

  return dto;
}

// ---- Service -----------------------------------------------------------------

@Injectable({
  providedIn: 'root',
})
export class HolidayService {
  private readonly baseUrl = `${environment.apiUrl}/holiday`;

  constructor(private readonly http: HttpClient) {}

  getHolidays(year?: number): Observable<HolidayModel[]> {
    let params = new HttpParams();
    if (year != null) {
      params = params.set('year', year.toString());
    }
    return this.http
      .get<HolidayDto[]>(this.baseUrl, { params })
      .pipe(map((dtos) => dtos.map(dtoToModel)));
  }

  getHoliday(id: number): Observable<HolidayModel> {
    return this.http
      .get<HolidayDto>(`${this.baseUrl}/${id}`)
      .pipe(map(dtoToModel));
  }

  createHoliday(data: Omit<HolidayModel, 'id'>): Observable<HolidayModel> {
    const dto = modelToDto(data);
    return this.http
      .post<HolidayDto>(this.baseUrl, dto)
      .pipe(map(dtoToModel));
  }

  updateHoliday(id: number, data: Partial<HolidayModel>): Observable<HolidayModel> {
    const merged: HolidayModel = { id, ...data } as HolidayModel;
    const dto = modelToDto(merged);
    return this.http
      .put<HolidayDto>(this.baseUrl, dto)
      .pipe(map(dtoToModel));
  }

  deleteHoliday(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }
}
